import { isScored } from '@/lib/scoreStatistics';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, eachDayOfInterval } from 'date-fns';

interface AgentPerformanceTrendsProps {
  companyId?: string | null;
}

export function AgentPerformanceTrends({ companyId }: AgentPerformanceTrendsProps) {
  const [selectedAgentId, setSelectedAgentId] = useState<string>('all');

  // Fetch agents for dropdown
  const { data: agents = [] } = useQuery({
    queryKey: ['agents-for-trends', companyId],
    queryFn: async () => {
      let query = supabase
        .from('simulation_agents')
        .select('id, name')
        .eq('status', 'active')
        .order('name');

      if (companyId) {
        query = query.or(`company_id.eq.${companyId},company_id.is.null`);
      }

      const { data } = await query;
      return data || [];
    },
  });

  // Fetch performance data
  const { data: trendData = [], isLoading } = useQuery({
    queryKey: ['agent-performance-trends', selectedAgentId, companyId],
    queryFn: async () => {
      const endDate = new Date();
      const startDate = subDays(endDate, 30);

      let query = supabase
        .from('simulation_evaluations')
        .select('agent_id, agent_name, overall_score, evaluated_at')
        .gte('evaluated_at', startDate.toISOString())
        .lte('evaluated_at', endDate.toISOString())
        .eq('is_submitted', true)
        .is('deleted_at', null)
        .not('overall_score', 'is', null);

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      if (selectedAgentId !== 'all') {
        query = query.eq('agent_id', selectedAgentId);
      }

      const { data } = await query.order('evaluated_at', { ascending: true });

      if (!data || data.length === 0) return [];

      // Group by date
      const dateMap = new Map<string, { total: number; count: number }>();
      
      // Initialize all days in range
      const allDays = eachDayOfInterval({ start: startDate, end: endDate });
      allDays.forEach(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        dateMap.set(dateStr, { total: 0, count: 0 });
      });

      // Aggregate scores by date
      data.filter(evaluation => isScored(evaluation.overall_score)).forEach((evaluation) => {
        const dateStr = format(new Date(evaluation.evaluated_at), 'yyyy-MM-dd');
        const existing = dateMap.get(dateStr) || { total: 0, count: 0 };
        dateMap.set(dateStr, {
          total: existing.total + (evaluation.overall_score || 0),
          count: existing.count + 1,
        });
      });

      // Convert to array
      const results = Array.from(dateMap.entries()).map(([date, value]) => ({
        date,
        displayDate: format(new Date(date), 'MMM d'),
        avgScore: value.count > 0 ? Math.round(value.total / value.count) : null,
        sessions: value.count,
      }));

      return results;
    },
  });

  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Trends
          </CardTitle>
          <CardDescription>
            {selectedAgentId === 'all' 
              ? 'Average score trends across all agents' 
              : `Score trends for ${selectedAgent?.name || 'selected agent'}`}
          </CardDescription>
        </div>
        <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select agent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : trendData.every(d => d.avgScore === null) ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            No performance data available
          </div>
        ) : (
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="displayDate" 
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={[0, 100]} 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number | null, name: string) => [
                    value !== null ? `${value}%` : 'No data',
                    'Avg Score'
                  ]}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="avgScore"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', r: 2 }}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
